import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';
import React, { useRef, useState } from 'react';

import { PHOTO_PACK_COVERS } from '@/constants/photo_pack_covers';
import { PhotoPack } from '@/constants/photo_packs';

interface PhotoPacksShowcaseProps {
  photoPacks: PhotoPack[];
  initialCount?: number;
  className?: string;
}

export default function PhotoPacksShowcase({
  photoPacks,
  initialCount = 100,
  className = '',
}: PhotoPacksShowcaseProps) {
  const { t } = useLocale('ai-photo-generator');
  const [expanded, setExpanded] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const displayPacks = expanded
    ? photoPacks
    : photoPacks.slice(0, initialCount);

  return (
    <section
      id='photo-packs'
      aria-labelledby='photo-packs-title'
      className={`mx-auto w-full max-w-6xl scroll-mt-24 space-y-8 bg-white px-4 py-8 dark:bg-slate-800 dark:text-slate-200 sm:rounded-lg sm:p-8 ${className}`}
    >
      <div className='text-center'>
        <p className='mb-4 text-xs font-medium uppercase tracking-widest'>
          {t('studio.pack_kicker')}
        </p>
        <h2
          ref={titleRef}
          id='photo-packs-title'
          tabIndex={-1}
          className='mb-4 scroll-mt-8 text-3xl font-bold'
        >
          {t('photo_packs_title', { count: photoPacks.length })}
        </h2>
        <p className='mx-auto max-w-2xl text-gray-600 dark:text-slate-400'>
          {t('photo_packs_description')}
        </p>
      </div>

      <div
        id='photo-pack-grid'
        className='grid grid-cols-2 gap-x-4 gap-y-7 text-start sm:grid-cols-3 lg:grid-cols-4'
      >
        {displayPacks.map((pack) => {
          const cover = PHOTO_PACK_COVERS[pack.slug] || pack.sample_images[0];
          const content = (
            <>
              <div className='relative aspect-[3/4] overflow-hidden rounded-lg bg-gray-100 dark:bg-slate-700'>
                {cover ? (
                  <Image
                    src={cover}
                    alt={pack.name}
                    fill
                    sizes='(max-width: 639px) calc((100vw - 56px) / 2), (max-width: 1023px) calc((100vw - 80px) / 3), (max-width: 1375px) calc((100vw - 144px) / 4), 308px'
                    className='object-cover transition-transform duration-300 motion-safe:group-hover:scale-[1.025] motion-reduce:transition-none'
                  />
                ) : null}
                {pack.is_premium ? (
                  <span className='absolute left-2 top-2 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-semibold tracking-wide text-gray-900'>
                    {t('photo_packs_premium')}
                  </span>
                ) : null}
              </div>
              <div className='pt-3'>
                <h3 className='break-words text-sm font-semibold leading-snug text-gray-900 dark:text-white sm:text-base'>
                  {pack.name}
                </h3>
                <p className='mt-1.5 line-clamp-2 text-xs leading-relaxed text-gray-600 dark:text-gray-400'>
                  {pack.description}
                </p>
                <p className='mt-2 text-xs font-medium text-primary-600 dark:text-primary-400'>
                  {t('photo_packs_photo_count', { count: pack.prompt_count })}
                </p>
              </div>
            </>
          );

          return pack.url ? (
            <a
              key={pack.slug}
              href={pack.url}
              className='group min-w-0 rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600'
            >
              {content}
            </a>
          ) : (
            <div key={pack.slug} className='group min-w-0'>
              {content}
            </div>
          );
        })}
      </div>
      {photoPacks.length > initialCount ? (
        <div className='flex justify-center pt-4'>
          <button
            type='button'
            aria-expanded={expanded}
            aria-controls='photo-pack-grid'
            onClick={() => {
              setExpanded(!expanded);
              if (expanded) {
                titleRef.current?.focus({ preventScroll: true });
                titleRef.current?.scrollIntoView({ block: 'start' });
              }
            }}
            className='rounded border border-current px-6 py-3 text-sm font-medium transition-colors hover:bg-black/5 dark:hover:bg-white/5'
          >
            {expanded
              ? t('studio.show_less')
              : t('studio.show_all', { count: photoPacks.length })}
          </button>
        </div>
      ) : null}
    </section>
  );
}
